import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

import type { createQuery } from '@shared/lib/query-helpers';

/** Adapts canonical numbered collections without introducing another wire envelope. */
export function useNumberedSelector<
  Page extends {
    data: { items: readonly unknown[] };
    meta: { page: number; hasNextPage: boolean };
  },
>(options: {
  queryKey: QueryKey;
  pageQuery: (page: number) => ReturnType<typeof createQuery<Page>>;
  itemKey: (item: Page['data']['items'][number]) => string;
  enabled: boolean;
}) {
  // Each key owns its locks; an old request cannot clear a new scope's lock.
  const scopeKey = JSON.stringify(options.queryKey);
  const inFlightPages = useMemo(() => new Set<number>(), [scopeKey]);
  const query = useInfiniteQuery({
    queryKey: options.queryKey,
    queryFn: async (context) => {
      const page =
        typeof context.pageParam === 'number' && Number.isInteger(context.pageParam)
          ? context.pageParam
          : 1;
      const pageQuery = options.pageQuery(page);
      if (typeof pageQuery.queryFn !== 'function') throw new Error('Selector query unavailable.');
      return await pageQuery.queryFn({ ...context, queryKey: pageQuery.queryKey });
    },
    initialPageParam: 1,
    getNextPageParam: (page: Page) => (page.meta.hasNextPage ? page.meta.page + 1 : undefined),
    enabled: options.enabled,
    gcTime: 5 * 60 * 1000,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  });
  const loadNextPage = useCallback(() => {
    const lastPage = query.data?.pages.at(-1);
    if (!options.enabled || !lastPage?.meta.hasNextPage || query.isFetching) return;
    const nextPage = lastPage.meta.page + 1;
    if (inFlightPages.has(nextPage)) return;
    inFlightPages.add(nextPage);
    void query.fetchNextPage({ cancelRefetch: false }).finally(() => {
      inFlightPages.delete(nextPage);
    });
  }, [inFlightPages, options.enabled, query]);
  const items = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages ?? [])
      .flatMap((page) => page.data.items)
      .filter((item) => {
        const key = options.itemKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }) as Page['data']['items'];
  }, [options.itemKey, query.data?.pages]);
  const retry = useCallback(() => {
    if (query.isFetchNextPageError) loadNextPage();
    else void query.refetch();
  }, [loadNextPage, query]);
  return { ...query, items, loadNextPage, retry };
}
